/**
 * ChatPromptFile component for the Indigo design system.
 *
 * A file attachment card used inside chat prompt bubbles to surface a
 * file (typically a PDF / medical record). The default size renders a
 * 36×36 icon tile on the left and a title / subtitle stack on the right;
 * the compact size mirrors DragAndDrop file tags for pending attachments.
 *
 * Caller-supplied: icon node, title, subtitle, and `onClick` handler. The
 * design system owns the layout, spacing, typography, and hover styling.
 *
 * @example
 * ```tsx
 * <ChatPromptFile
 *   icon={<Icon name="pdf" size="m" />}
 *   title="Full medical record.pdf"
 *   subtitle="148 pages"
 *   onClick={() => openFile(id)}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, KeyboardEvent, ReactNode } from "react";
import { Icon } from "../../Icons";
import { Body } from "../../Typography/Body";
import "./ChatPromptFile.css";

export type ChatPromptFileSize = "default" | "compact";

export interface ChatPromptFileProps {
  /** Icon node rendered inside the 36×36 tile. */
  icon: ReactNode;
  /** Title text. Rendered as Body S in primary color. */
  title: string;
  /** Subtitle text. Rendered as Body XS in tertiary color. */
  subtitle?: string;
  /** Click handler. Renders the card as a button when provided. */
  onClick?: () => void;
  /** Optional remove action rendered on the right side of the card. */
  onRemove?: () => void;
  /** Accessible label for the remove action. */
  removeLabel?: string;
  /** Visual size variant. Use compact for pending files above chat inputs. */
  size?: ChatPromptFileSize;
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

export const ChatPromptFile = forwardRef<HTMLElement, ChatPromptFileProps>(
  (
    {
      icon,
      title,
      subtitle,
      onClick,
      onRemove,
      removeLabel,
      size = "default",
      className,
      ...rest
    },
    ref,
  ) => {
    const dataTestId = rest["data-testid"];
    const isInteractive = Boolean(onClick);
    const hasRemoveAction = Boolean(onRemove);

    const rootClass = clsx(
      "indigo-chat-prompt-file",
      size === "compact" && "indigo-chat-prompt-file--compact",
      isInteractive && "indigo-chat-prompt-file--button",
      className,
    );

    const handleDivKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isInteractive) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onClick?.();
    };

    const content = (
      <>
        <span className="indigo-chat-prompt-file__icon-wrap">{icon}</span>
        <span className="indigo-chat-prompt-file__body">
          <Body as="span" size="sm" className="indigo-chat-prompt-file__title">
            {title}
          </Body>
          {subtitle && (
            <Body
              as="span"
              size="xs"
              className="indigo-chat-prompt-file__subtitle"
            >
              {subtitle}
            </Body>
          )}
        </span>
        {onRemove && (
          <button
            type="button"
            className="indigo-chat-prompt-file__remove"
            aria-label={removeLabel ?? `Remove ${title}`}
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          >
            <Icon name="close" sizeX={size === "compact" ? 12 : 14} />
          </button>
        )}
      </>
    );

    if (isInteractive && !hasRemoveAction) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          className={rootClass}
          data-testid={dataTestId}
          onClick={onClick}
        >
          {content}
        </button>
      );
    }

    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={rootClass}
        data-testid={dataTestId}
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onClick={isInteractive ? onClick : undefined}
        onKeyDown={isInteractive ? handleDivKeyDown : undefined}
      >
        {content}
      </div>
    );
  },
);

ChatPromptFile.displayName = "ChatPromptFile";

export default ChatPromptFile;
